import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import {
  Button,
  feedbackcard,
  SkeletonDots,
  StandardUniselection
} from '@yes.technology/react-toolkit'

import { useDocumentTitle } from 'infrastructure/shared/hooks'
import SuggestionChangePassword from 'authentication/Login/LoginContainer/SuggestionChangePassword'
import AuthenticationTemplate from 'authentication/shared/components/AuthenticationTemplate'
import usePasswordChangeSuggestion from 'authentication/shared/hooks/usePasswordChangeSuggestion'
import { CredentialOptions } from 'authentication/shared/types'
import Title from 'shared/components/Title'
import { getEcosystemName } from 'shared/utils/ecosystem'
import useCredentials from './useCredentials'
import {
  useAuthentication,
  useRedirectOrigin
} from 'authentication/shared/hooks'

const Credentials = () => {
  const { t } = useTranslation()

  const ecosystemOption = useMemo(() => {
    const ecosystem = getEcosystemName()
    return { id: ecosystem, value: ecosystem, label: ecosystem }
  }, [])

  const [shouldDisableSubmit, setShouldDisableSubmit] = useState(false)

  const { redirectToOrigin } = useRedirectOrigin()

  const { authenticatedData } = useAuthentication()

  const { shouldOpenPasswordChange, handleSuggestPasswordChange } =
    usePasswordChangeSuggestion()

  const {
    credentials,
    submitCredentials,
    selectedCredentials,
    isCredentialsValid,
    isLoading: isFetchingCredentials,
    setEcosystem,
    setField
  } = useCredentials()

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    setShouldDisableSubmit(true)

    const onSuccess = async (upgradedToken: string) => {
      feedbackcard(t('credentials.feedback.success.title'), {
        message: t('credentials.feedback.success.msg'),
        type: 'success'
      })

      const isRedirectToOrigin = !handleSuggestPasswordChange(authenticatedData)

      if (isRedirectToOrigin) {
        redirectToOrigin(upgradedToken)
      } else {
        setShouldDisableSubmit(false)
      }
    }

    const onError = () => setShouldDisableSubmit(false)

    await submitCredentials(onSuccess, onError)
  }

  useDocumentTitle({ title: t('credentials.title') })

  return (
    <AuthenticationTemplate>
      <Title>{t('credentials.title')}</Title>

      {isFetchingCredentials && <SkeletonDots />}

      {!isFetchingCredentials && credentials && (
        <form
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          onSubmit={handleSubmit}
        >
          <StandardUniselection
            label={t('active-session.ecosystem-credentials')}
            options={[ecosystemOption]}
            selectedOption={ecosystemOption}
            onSelect={() => {
              setEcosystem(ecosystemOption.id)
            }}
            heightClass='regular'
            required
          />
          <StandardUniselection
            label={t('active-session.applicationsystem-credential')}
            placeholder={t('credentials.select-application-system')}
            options={credentials.applicationSystem.options}
            selectedOption={credentials.applicationSystem?.options.find(
              (option) =>
                option.id === selectedCredentials.applicationsystem_uuid
            )}
            onSelect={(selected) => {
              setField(
                (selected as CredentialOptions)?.id,
                'applicationsystem_uuid'
              )
            }}
            heightClass='regular'
            required
          />
          <StandardUniselection
            label={t('active-session.organization-credential')}
            placeholder={t('credentials.select-organization-credential')}
            options={credentials.organization.options}
            selectedOption={credentials.organization?.options.find(
              (option) => option.id === selectedCredentials.organization_uuid
            )}
            onSelect={(selected) => {
              setField((selected as CredentialOptions)?.id, 'organization_uuid')
            }}
            heightClass='regular'
            required
          />
          <StandardUniselection
            label={t('active-session.user-credential')}
            placeholder={t('credentials.select-user-credential')}
            options={credentials.user.options}
            selectedOption={credentials.user?.options.find(
              (option) => option.id === selectedCredentials.user_uuid
            )}
            onSelect={(selected) => {
              setField((selected as CredentialOptions)?.id, 'user_uuid')
            }}
            heightClass='regular'
            required
          />
          <Button
            disabled={shouldDisableSubmit || !isCredentialsValid}
            impactClass='workflow'
            type='submit'
          >
            {t('credentials.apply')}
          </Button>
        </form>
      )}
      <SuggestionChangePassword isOpen={shouldOpenPasswordChange} />
    </AuthenticationTemplate>
  )
}

export default Credentials
